/**
 * English copy for the six comparison pages.
 *
 * Every claim here is either a fact about this repository — checkable by
 * reading it — or a statement about the other product so general that it will
 * still be true next year. There are no competitor prices, no competitor
 * feature lists and no screenshots of anybody else's app, and that is a
 * deliberate limit rather than an oversight: see `types.ts`.
 *
 * The four dimensions repeat across all six pages on purpose. They are the ones
 * that actually differ in this category and the ones a reader is choosing
 * between — where the photo goes, whether the number can be checked, what it
 * costs to try, and how many languages it speaks. Six pages that each invented
 * their own dimensions would be six pages nobody could compare.
 */

import type { CompareCopy, CompareSlug } from "./types";

/** The rows every page shares. Facts about this repo, kept true by it. */
const SHARED_ROWS = [
  {
    dimension: "Where your photo is processed",
    fazly:
      "The free tools run Google's MediaPipe face mesh as WebAssembly in your browser. The image is never transmitted — there is no upload endpoint for it, which you can confirm in your browser's network tab. The app uploads scans, and any scan can be deleted.",
  },
  {
    dimension: "Whether you can check the number",
    fazly:
      "Every tool shows the measurement, the target it was scored against, and the weights behind the composite. The face-shape detector shows the four ratios that produced its verdict, so you can disagree with it on the evidence.",
  },
  {
    dimension: "What it costs to try",
    fazly:
      "The eleven browser tools are free and need no account, no email and no install. The app is free to download and free to scan; Pro adds the full breakdown, the roadmap and a new scan every week.",
  },
  {
    dimension: "Languages",
    fazly:
      "Five, fully: English, Turkish, German, Spanish and Portuguese. Not a machine-translated shell — the tool copy, the bands and the questions are written in each.",
  },
  {
    dimension: "What the scores claim to be",
    fazly:
      "Distances from a stated convention, said so on every page. Fazly does not claim its targets are findings about attractiveness, because they are not.",
  },
] as const;

const CAVEAT =
  "This page states what Fazly does, which is checkable from this site. It does not list the other app's prices or features, because those change and a comparison page that is wrong about a competitor is worse than no comparison page at all — check their own site for their current answer.";

export const en: Record<CompareSlug, CompareCopy> = {
  umax: {
    rival: "UMAX",
    title: "Fazly vs UMAX — Which AI Face Analysis App? | Fazly",
    description:
      "An honest comparison of Fazly and UMAX on the things that actually differ: where the photo goes, whether the score can be checked, what it costs to try, and how many languages each speaks.",
    h1: "Fazly vs UMAX",
    standfirst:
      "Four dimensions that actually differ, and what Fazly does about each.",
    answer:
      "UMAX is the app that made AI face rating a category, and it reached that position through TikTok rather than through search. Fazly arrived later and is built around a different bet: that the number is only worth having if you can see what produced it. The practical differences a reader is choosing between are four. Where the photo is processed — Fazly's eleven browser tools run entirely on your device and never transmit the image. Whether the score can be checked — every Fazly measurement is shown with its target and its weight, and the face-shape verdict shows the four ratios behind it. What it costs to try — the browser tools need no account, no email and no install. And languages — Fazly is written, not machine-translated, in five. Check UMAX's own site for its current answers; they change.",
    bestForFazly:
      "You want to measure something before installing anything, and you want to see the arithmetic behind the number.",
    bestForRival:
      "You want the app the category is named after, with the largest community around it and the most familiar result format.",
    theirStrength:
      "Reach and recognition. UMAX defined how these results look and read, and a result format most people already recognise is genuinely easier to interpret than a new one.",
    rows: SHARED_ROWS,
    caveat: CAVEAT,
    faq: [
      {
        q: "Is Fazly free?",
        a: "The eleven measurement tools on fazly.app are free with no account at all — no email, no install, nothing to cancel. The app is separately free to download and free to scan, with a Pro tier for the full breakdown and the roadmap.",
      },
      {
        q: "Which one is more accurate?",
        a: "Neither claim is checkable, because there is no ground truth for facial attractiveness to be accurate about. What is checkable is whether a tool shows its working. Fazly does, on every measurement, which is the only comparison of this kind worth making.",
      },
      {
        q: "Do I need to pick one?",
        a: "No, and running the same photo through both is the most useful thing you can do with an afternoon. Where two tools disagree, the one that shows the ratios behind its answer is the one you can actually investigate.",
      },
    ],
  },

  qoves: {
    rival: "Qoves",
    title: "Fazly vs Qoves — Free Tools or a Paid Report? | Fazly",
    description:
      "Fazly and Qoves solve different problems: instant on-device measurement versus a detailed written facial-aesthetics report. An honest look at which one your question needs.",
    h1: "Fazly vs Qoves",
    standfirst:
      "A measurement in your browser, or a written report. Different products.",
    answer:
      "Qoves comes from the facial-aesthetics and cosmetic-consultation tradition: its reputation is built on detailed written analysis, closer to what a clinician would produce than to what an app returns. Fazly is a measuring instrument. That difference decides which one your question needs. If you want to know your gonial angle, your canthal tilt or your face shape right now, with the ratios that produced each, Fazly's browser tools give you that in seconds without an account and without the photo leaving your device. If you want a considered written interpretation of your face — what a change would actually involve, in prose, from someone who does this professionally — that is not what a browser tool is, and Fazly does not pretend otherwise. Check Qoves's own site for what they currently offer, because a comparison page is the wrong place to learn somebody else's prices.",
    bestForFazly:
      "You want measurements now, free, on your own device, with the arithmetic visible and a daily routine attached.",
    bestForRival:
      "You want a detailed written interpretation of your face from people who do facial aesthetics professionally, and you are willing to pay for it.",
    theirStrength:
      "Depth of written analysis. A measurement tells you a number; a considered report tells you what it means for your face specifically, and no automated tool does that well.",
    rows: SHARED_ROWS,
    caveat: CAVEAT,
    faq: [
      {
        q: "Can Fazly replace a professional facial analysis?",
        a: "No, and it does not try. Fazly measures distances and angles and scores them against stated conventions. A professional assessment interprets a face in context, which is a different activity that a browser cannot do.",
      },
      {
        q: "Why is Fazly free when detailed reports are not?",
        a: "Because they are different products. A measurement is arithmetic and costs almost nothing to run once the model is loaded on your device. A written report is somebody's time.",
      },
      {
        q: "Which should I do first?",
        a: "Measure first. It is free, it takes a minute, and knowing your actual ratios is what lets you tell whether a paid report is telling you something you did not already know.",
      },
    ],
  },

  mirror: {
    rival: "Mirror",
    title: "Fazly vs Mirror — AI Face Rating Compared | Fazly",
    description:
      "How Fazly and Mirror differ on the four things that matter: on-device processing, whether the score shows its working, what it costs to try, and language coverage.",
    h1: "Fazly vs Mirror",
    standfirst: "Two face-rating apps, four dimensions that actually differ.",
    answer:
      "Mirror is one of several AI face-rating apps that arrived after the category was established, and like most of them it returns a score and a breakdown from a model you cannot inspect. Fazly's difference is not that its model is better — that is not a claim anybody in this category can support — but that its arithmetic is visible. Every measurement carries the distance it measured, the target it was scored against and the weight it contributes; the face-shape verdict shows the four ratios behind it and the margin over the runner-up rather than a fabricated confidence percentage. The eleven browser tools also run entirely on your device with no account, so you can measure something before deciding whether any of this is for you. Check Mirror's own site for its current features and pricing.",
    bestForFazly:
      "You do not trust a number you cannot check, and you want to try the measurements before installing anything.",
    bestForRival:
      "You want a single polished app experience and are not interested in the working behind the numbers.",
    theirStrength:
      "Simplicity. A single score with no ratios underneath it is easier to read, and for a lot of people that is the right trade.",
    rows: SHARED_ROWS,
    caveat: CAVEAT,
    faq: [
      {
        q: "Do these apps use the same technology?",
        a: "Most face-rating apps build on a facial-landmark model and score the geometry it produces; Fazly uses Google's MediaPipe face mesh and says so. The differences between them are in which measurements they take, what targets they score against, and whether they tell you.",
      },
      {
        q: "Why do two apps give me different scores?",
        a: "Because they score against different targets and weight them differently, and almost none of them publish either. Fazly publishes both, which is why its score is arguable rather than merely different.",
      },
      {
        q: "Is a higher score from one app more accurate?",
        a: "No. A score is a distance from whatever convention that app chose, so comparing scores across apps compares the conventions rather than the faces. The only cross-app comparison that means anything is whether each one tells you what it measured.",
      },
    ],
  },

  "looksmax-ai": {
    rival: "LooksMax AI",
    title: "Fazly vs LooksMax AI — An Honest Comparison | Fazly",
    description:
      "Fazly and LooksMax AI compared on where your photo goes, whether the score can be checked, what it costs to try, and how many languages each actually speaks.",
    h1: "Fazly vs LooksMax AI",
    standfirst:
      "Same category, different answers on privacy and on showing the working.",
    answer:
      "LooksMax AI and Fazly target the same searches and the same audience, so the useful comparison is not which one is better but where they actually differ. Fazly's eleven browser tools run the face mesh as WebAssembly on your own device: the photo is never transmitted, and you can confirm that in the network tab rather than take it on trust. Every measurement is shown with the target it was scored against and the weight it carries, so a low score points at something specific instead of being a verdict. There is no account, no email and nothing to install to use them. And the site is written in five languages rather than machine-translated into them. Check LooksMax AI's own site for its current answers on each of these, because they change and this page is not the place to learn them.",
    bestForFazly:
      "You want to measure without uploading, without an account, and with the ratios visible.",
    bestForRival:
      "You want the specific result format and community that app has built around itself.",
    theirStrength:
      "Focus. An app that does one thing and presents one number is a clearer product than one that shows you eight measurements and their weights.",
    rows: SHARED_ROWS,
    caveat: CAVEAT,
    faq: [
      {
        q: "Which app is better for looksmaxxing?",
        a: "For the part that responds to effort — skin, sleep, body composition, grooming, posture — what matters is whether the app turns a measurement into a routine you actually work. Fazly names one focus area and builds a thirty-day plan around it rather than handing you eight things to fix.",
      },
      {
        q: "Does Fazly upload my photo?",
        a: "The browser tools do not: the mesh runs locally and there is no endpoint for the image. The app does upload scans so it can track them over time, and any scan can be deleted, as can the whole account.",
      },
      {
        q: "Can I use both?",
        a: "Yes, and it is the most informative thing to do. Where two tools disagree, the one that shows its ratios is the one where you can find out why.",
      },
    ],
  },

  "cal-ai": {
    rival: "Cal AI",
    title: "Fazly vs Cal AI — Face Scanning or Calorie Scanning? | Fazly",
    description:
      "Cal AI scans food and Fazly scans faces. If you found this comparison, here is what each actually does and why they are not alternatives.",
    h1: "Fazly vs Cal AI",
    standfirst:
      "These are not alternatives. One scans meals, the other scans faces.",
    answer:
      "Cal AI is a calorie-tracking app: you photograph a meal and it estimates what is in it. Fazly measures a face. They appear in the same conversations because both are AI scanning apps that grew through TikTok, and because body composition genuinely is one of the things that changes how a face reads — but they are not substitutes and nobody should be choosing between them. If you are here because you want to change how your face looks, the honest answer is that both may be useful: Fazly measures and tracks the face and builds the daily routine, and a food tracker addresses one of the inputs. Fazly's browser tools measure your symmetry, jawline, face shape and six more, free and on your own device. Check Cal AI's own site for what it currently does.",
    bestForFazly:
      "You want to measure and track your face, and work a routine built around the weakest measurement.",
    bestForRival:
      "You want to track what you eat. That is a different problem and Fazly does not address it.",
    theirStrength:
      "It solves a problem Fazly does not touch. Body composition is one of the largest inputs to how a face reads, and Fazly measures the result rather than the cause.",
    rows: SHARED_ROWS,
    caveat: CAVEAT,
    faq: [
      {
        q: "Is Cal AI an alternative to Fazly?",
        a: "No. Cal AI estimates the contents of a meal from a photograph; Fazly measures facial proportions from one. If you are comparing them you are probably looking for one of the two, not choosing between them.",
      },
      {
        q: "Does losing weight change my face scores?",
        a: "Some of them, substantially. Jaw width and fWHR both move with fat over the cheeks and jaw without any change to the bone underneath, which is why Fazly's copy says those two are the measurements most affected by body composition.",
      },
      {
        q: "Which one should I install?",
        a: "Whichever matches the question you actually have. If it is about your face, start with Fazly's free browser tools — they need no install at all.",
      },
    ],
  },

  aire: {
    rival: "Aire",
    title: "Fazly vs Aire — AI Face Analysis Compared | Fazly",
    description:
      "How Fazly compares to Aire on the four dimensions that differ between face-analysis apps: on-device processing, visible arithmetic, free access, and real language coverage.",
    h1: "Fazly vs Aire",
    standfirst: "Where the photo goes, and whether you can check the number.",
    answer:
      "Aire is another entrant in AI face analysis, and as with the rest of the category the interesting question is not whose model is better — nobody can support that claim — but what each app is willing to show you. Fazly's position is that a score you cannot check is a slot machine. So every measurement on this site carries the distance it measured, the convention it was scored against and the weight it contributes to the composite; the face-shape verdict shows its four ratios and reports a margin over the runner-up rather than a confidence percentage nobody calibrated. The eleven browser tools run the face mesh on your own device with no account and no upload, so none of this requires trusting us first. Check Aire's own site for its current answers on privacy, pricing and features.",
    bestForFazly:
      "You want to see the measurement, not just the verdict, and to try it without handing over a photo or an email.",
    bestForRival:
      "You prefer their interface or their result format, which is a legitimate reason to pick an app.",
    theirStrength:
      "A newer product can make cleaner interface decisions than one carrying a live user base, and interface is most of what using an app feels like.",
    rows: SHARED_ROWS,
    caveat: CAVEAT,
    faq: [
      {
        q: "What should I actually compare face apps on?",
        a: "Three things that are checkable: whether your photo leaves your device, whether the app shows the measurement behind the score, and what it costs to find out. Everything else in this category is marketing about a model nobody can inspect.",
      },
      {
        q: "Does Fazly work without an account?",
        a: "The eleven tools on this site do, entirely. No email, no sign-up, no install. The app needs no sign-up either — it identifies a device rather than a person.",
      },
      {
        q: "How often should I rescan?",
        a: "Weekly at most, in the same light and the same pose. The measurements move more with camera angle than with anything you did in seven days, so a daily rescan measures your photography.",
      },
    ],
  },
};
